import React, { ChangeEventHandler, ForwardedRef, forwardRef, type JSX } from "react"
import classNames from "classnames"
import "./form-text-input.css"
import { useTranslation } from "@nova/use-translation"

type FormTextInputProps = {
    label?: string
    name?: string
    id?: string
    type?: string
    placeholder?: string
    value?: string
    onChange?: ChangeEventHandler<HTMLInputElement>
    width?: string
    error?: string
    required?: boolean
    disabled?: boolean
    autoComplete?: string
    defaultValue?: string
    className?: string
    labelClassName?: string
    inputClassName?: string
    inputContainerClassName?: string
    hideError?: boolean
    rightIcon?: JSX.Element
    rightIconFocusedOnly?: boolean
    noErrorBorder?: boolean
    autoFocus?: boolean
}

/**
 * @deprecated Use @nova/ds-component-input instead.
 */
export const FormTextInput = forwardRef(
    (
        {
            label,
            name,
            id,
            type = "text",
            placeholder,
            value,
            onChange,
            error,
            required,
            disabled,
            autoComplete,
            defaultValue,
            className,
            labelClassName,
            inputClassName,
            inputContainerClassName,
            hideError,
            rightIconFocusedOnly,
            rightIcon,
            noErrorBorder,
            autoFocus,
        }: FormTextInputProps,
        ref: ForwardedRef<HTMLInputElement>
    ) => {
        const { t } = useTranslation()
        const formLabel = required ? `${label} *` : label

        return (
            <div className={classNames(className)}>
                {label && formLabel && (
                    <h5
                        className={classNames("text-gray-100 mb-2", labelClassName)}
                        data-testid="form-text-input-label"
                    >
                        {t(formLabel)}
                    </h5>
                )}

                <div
                    className={classNames(
                        "flex items-center rounded-[6px] gap-1 pl-4 pr-2 flex-1",
                        "bg-gray-700 focus-within:bg-gray-500",
                        !noErrorBorder && error && "border border-solid border-red-500",
                        inputContainerClassName
                    )}
                >
                    <input
                        id={id}
                        name={name}
                        ref={ref}
                        type={type}
                        defaultValue={defaultValue}
                        value={value}
                        autoFocus={autoFocus}
                        className={classNames(
                            "peer flex-1 py-2 px-0 bg-transparent outline-none ring-0 focus:ring-0 focus:outline-none border-none text-gray-0 max-w-full",
                            "input",
                            inputClassName
                        )}
                        onChange={onChange}
                        placeholder={placeholder}
                        disabled={disabled}
                        required={required}
                        autoComplete={autoComplete}
                        data-testid="form-text-input-field"
                    />
                    {rightIcon && (
                        <div
                            className={classNames(
                                rightIconFocusedOnly && "hidden peer-focus:visible peer-focus:block"
                            )}
                            onMouseDown={(e) => {
                                e.preventDefault()
                            }}
                        >
                            {rightIcon}
                        </div>
                    )}
                </div>

                {!hideError && error && (
                    <small className="text-red-500" data-testid="form-text-input-error">
                        {t(error)}
                    </small>
                )}
            </div>
        )
    }
)
